import { useCallback, useEffect, useState } from "react";
import { Skeleton } from "../components/Ui";
import { text } from "../api/text";
import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { HowItWorks } from "../components/HowItWorks";

/**
 * Institute settings — SRS §13.9, FR-CFG-001..005.
 *
 * These values decide when a student is warned, what counts as complete and
 * what a certificate requires. The screen is built around three things a
 * settings page usually gets wrong:
 *
 *   WHERE THE VALUE CAME FROM is shown on every row. An administrator who
 *   changes the institute figure and sees nothing happen has almost always hit
 *   a more specific override; without being told, they conclude the feature is
 *   broken and change it again.
 *
 *   WHAT CHANGING IT DOES is written next to it, not in a manual. "0.15" and
 *   "attendance.lateWeight" together tell nobody anything.
 *
 *   NOTHING SAVES UNTIL IT IS PRESSED, and the default is always visible, so
 *   the way back is never lost.
 *
 * An Admin may read this and not change it (§4.5): the inputs are disabled
 * rather than the page refused, because seeing the Institute's policy is
 * legitimately part of administering it.
 */

type SettingType = "number" | "percent" | "boolean" | "string" | "string[]" | "weights";

interface Setting {
  key: string;
  type: SettingType;
  description: string;
  default: unknown;
  value?: unknown;
  source: string;
  isOverridden: boolean;
  overridableAt: string[];
  min?: number;
  max?: number;
  allowed?: string[];
  maxLength?: number;
  multiline?: boolean;
  isSecret?: boolean;
  isSet?: boolean;
}

interface Group {
  group: string;
  settings: Setting[];
}

export function SettingsPage() {
  const { hasRole } = useAuth();
  const mayConfigure = hasRole("super_admin");
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    api
      .get<Group[]>("/settings")
      .then(setGroups)
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load settings."));
  }, []);

  useEffect(load, [load]);

  /**
   * Groups with only the matching settings in them, and empty groups dropped.
   *
   * Matching on the key as well as the label and description is the point: an
   * administrator looking for "how long before a student is warned" does not
   * know it is called attendance.warningThreshold, but somebody reading a
   * support message quoting that key needs to find it by name.
   */
  const needle = filter.trim().toLowerCase();
  const visible = (groups ?? [])
    .map((g) => ({
      ...g,
      settings: needle
        ? g.settings.filter((s) =>
            [s.key, s.description, g.group].some((t) => t?.toLowerCase().includes(needle)),
          )
        : g.settings,
    }))
    .filter((g) => g.settings.length > 0);

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Settings</h1>
          <p className="muted small">
            Institute policy. A change applies from now on — decisions already made are
            unchanged.
          </p>
        </div>
        {/*
          A filter, because there are now forty-odd settings across ten groups
          and the only way to find one was to scroll and read. It matches the
          KEY and the DESCRIPTION as well as the label: an administrator
          looking for "how long before a student is warned" does not know the
          setting is called attendance.warningThreshold, but the description
          says what it does.
        */}
        <label className="field field-inline">
          <span className="visually-hidden">Find a setting</span>
          <input
            type="search"
            placeholder="Find a setting…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </label>
      </header>

      <HowItWorks
        id="settings"
        title="Institute policy"
        intro="The rules the whole System applies: when a student is warned, what counts as complete, what a certificate needs."
        steps={[
          { icon: "search", title: "Find the setting", body: "Search matches the name and the description, so you can look for what it does." },
          { icon: "book", title: "Read what it changes", body: "Every setting says in words what moving it will do." },
          { icon: "pen", title: "Change it", body: "Nothing saves until you press Save. The default is always shown beside it." },
          { icon: "clock", title: "It applies from now on", body: "Decisions already made stay made — lowering a threshold does not un-warn anybody." },
        ]}
        note="If you change something and nothing happens, look at where the value came from. A course or batch may have its own override, and the more specific one wins."
      />

      {!mayConfigure && (
        <div className="alert alert-warn">
          <p>
            You can read these. Changing them is reserved to a Super Admin, because they
            decide when a student is warned and what a certificate requires.
          </p>
        </div>
      )}

      {error && (
        <div className="alert alert-error" role="alert">
          <p>{error}</p>
        </div>
      )}

      {saved && (
        <div className="alert">
          <p>{saved}</p>
        </div>
      )}

      {!groups ? (
        <Skeleton lines={2} />
      ) : visible.length === 0 ? (
        <div className="card">
          <p className="muted">
            Nothing matches “{filter}”. The search covers the setting's name, its key and its
            description.
          </p>
        </div>
      ) : (
        visible.map((g) => (
          <section className="card" key={g.group}>
            <h2>
              {g.group}
              {/* The count moves with the filter, so it always describes what
                  is on screen rather than what exists. */}
              <span className="muted small setting-count">
                {g.settings.length} {g.settings.length === 1 ? "setting" : "settings"}
              </span>
            </h2>
            <ul className="list">
              {g.settings.map((s) => (
                <SettingRow
                  key={s.key}
                  setting={s}
                  canEdit={mayConfigure}
                  onSaved={(message) => {
                    setSaved(message);
                    setError(null);
                    load();
                  }}
                  onError={(message) => {
                    setError(message);
                    setSaved(null);
                  }}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </>
  );
}

function SettingRow({
  setting,
  canEdit,
  onSaved,
  onError,
}: {
  setting: Setting;
  canEdit: boolean;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState(() => toDraft(setting));
  const [busy, setBusy] = useState(false);

  // A reload after saving must not leave the box showing what was typed on a
  // row that failed, nor stale text on one that succeeded.
  useEffect(() => setDraft(toDraft(setting)), [setting]);

  const dirty = draft !== toDraft(setting);

  const save = async () => {
    setBusy(true);
    try {
      const value = fromDraft(setting, draft);
      await api.put(`/settings/${setting.key}`, { value });
      onSaved(`${setting.key} saved. It applies from now on.`);
    } catch (e) {
      onError(
        e instanceof ApiError
          ? (e.details?.[0]?.message ?? e.message)
          : e instanceof Error
            ? e.message
            : "Could not save it.",
      );
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.del(`/settings/${setting.key}`);
      onSaved(`${setting.key} restored to its default.`);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Could not restore it.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="assignment">
      <div className="assignment-head">
        <span>
          <strong>{label(setting.key)}</strong>
          <br />
          <span className="muted small">{setting.key}</span>
        </span>
        <span className="row-actions">
          {/* The whole reason a no-op change is explicable. */}
          {setting.source === "default" ? (
            <span className="muted small">default</span>
          ) : (
            <span className="pill">set at {setting.source.toLowerCase()}</span>
          )}
        </span>
      </div>

      <p className="muted small">{setting.description}</p>

      {setting.isSecret ? (
        // SEC-CRY-010 — never read back. Not masked: absent.
        <p className="muted small">
          {setting.isSet ? "A value is set. It cannot be shown, only replaced." : "Not set."}
        </p>
      ) : (
        <div className="field-row">
          <label className="field">
            <span>Value</span>
            {setting.type === "boolean" ? (
              <select value={draft} disabled={!canEdit} onChange={(e) => setDraft(e.target.value)}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : setting.type === "number" || setting.type === "percent" ? (
              <input
                type="number"
                value={draft}
                disabled={!canEdit}
                {...(setting.min !== undefined ? { min: setting.min } : {})}
                {...(setting.max !== undefined ? { max: setting.max } : {})}
                step="any"
                onChange={(e) => setDraft(e.target.value)}
              />
            ) : isProse(setting) ? (
              /*
               * A BOX THE SIZE OF THE VALUE.
               *
               * The public page's headline, its paragraph and its six feature
               * cards are edited here as well as on their own screen, and each
               * one is prose or a list of lines. A single-line input shows
               * forty characters of a four-hundred-character sentence and
               * offers no way to type a newline at all — so a list of six
               * cards could be READ here and never correctly written.
               */
              <textarea
                rows={setting.type === "string[]" ? 5 : 3}
                value={draft}
                disabled={!canEdit}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={hint(setting)}
              />
            ) : (
              <input
                value={draft}
                disabled={!canEdit}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={hint(setting)}
              />
            )}
          </label>
          <span className="muted small">
            default {format(setting.default)}
            {setting.min !== undefined && setting.max !== undefined
              ? ` · ${setting.min}–${setting.max}`
              : ""}
          </span>
        </div>
      )}

      {canEdit && (
        <div className="row-actions">
          <button className="btn btn-primary" onClick={() => void save()} disabled={busy || !dirty}>
            {busy ? "Saving…" : "Save"}
          </button>
          {setting.isOverridden && (
            <button className="btn btn-quiet" onClick={() => void reset()} disabled={busy}>
              Restore the default
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/** "attendance.warningThreshold" -> "Warning threshold". */
function label(key: string): string {
  const last = key.split(".").pop() ?? key;
  const spaced = last.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A LIST OF WORDS AND A LIST OF LINKS ARE NOT THE SAME KIND OF LIST, and
 * treating them as one silently destroyed the second.
 *
 * `allowed` marks a fixed vocabulary — the file extensions an upload may have.
 * Those are short, lowercase by definition, and comma-separated reads naturally.
 *
 * Everything else in a `string[]` is free text: a URL, or a feature card
 * written as `icon | title | body`. Both routinely CONTAIN a comma, and a URL
 * is case-sensitive — a YouTube id lowercased is a video that does not exist.
 * So those are one per line, exactly as typed.
 */
function isVocabulary(setting: Setting): boolean {
  return setting.type === "string[]" && !!setting.allowed;
}

/** Rendered in a box rather than on a line: prose, or a list of lines. */
function isProse(setting: Setting): boolean {
  if (setting.type === "string[]") return !isVocabulary(setting);
  return setting.type === "string" && setting.multiline === true;
}

function hint(setting: Setting): string {
  if (isVocabulary(setting)) return "Comma separated, e.g. pdf, docx, png";
  if (setting.type === "string[]") return "One per line";
  if (setting.type === "weights") return "video 30, assignment 30, quiz 25, attendance 15";
  return "";
}

function format(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([k, v]) => `${k} ${Math.round(Number(v) * 100)}%`)
      .join(", ");
  }
  return String(value);
}

/** The value as text a person can edit. */
function toDraft(setting: Setting): string {
  const value = setting.value ?? setting.default;
  if (setting.type === "weights") {
    // Shown and typed as PERCENTAGES. The API stores fractions summing to 1,
    // and "0.25" is a worse thing to ask somebody to type than "25".
    return Object.entries((value ?? {}) as Record<string, number>)
      .map(([k, v]) => `${k} ${Math.round(v * 100)}`)
      .join(", ");
  }
  // One per line unless it is a fixed vocabulary — see isVocabulary.
  if (Array.isArray(value)) return value.join(isVocabulary(setting) ? ", " : "\n");
  return text(value);
}

/** Back to what the API expects. Throws with a readable message on nonsense. */
function fromDraft(setting: Setting, draft: string): unknown {
  switch (setting.type) {
    case "number":
    case "percent": {
      const n = Number(draft);
      if (!Number.isFinite(n)) throw new Error(`${setting.key} must be a number.`);
      return n;
    }
    case "boolean":
      return draft === "true";
    case "string[]":
      /*
       * THIS USED TO LOWERCASE EVERY ENTRY AND SPLIT ON EVERY COMMA, which was
       * right for `pdf, docx, png` and wrong for everything else the System
       * stores as a list. A YouTube share link pasted here came back with its
       * id lowercased — a different video, or none — and a feature card, which
       * is a sentence, was shattered at each of its commas. Both failures were
       * silent: the value saved, the screen showed it saved, and the public
       * page quietly stopped working.
       */
      if (isVocabulary(setting)) {
        return draft
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
      }
      return draft
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    case "weights": {
      // "video 30, assignment 30, quiz 25, attendance 15" -> fractions.
      const out: Record<string, number> = {};
      for (const part of draft.split(",")) {
        const [name, amount] = part.trim().split(/\s+/);
        if (!name) continue;
        const n = Number(amount);
        if (!Number.isFinite(n)) {
          throw new Error(`"${part.trim()}" should be a component and a percentage, e.g. quiz 25.`);
        }
        out[name] = n / 100;
      }
      return out;
    }
    default:
      return draft;
  }
}
